/**
 * Operational date selection — sets the working day for pump operations.
 */
import React, { useEffect, useState } from 'react';
import Sidebar_Progress from "../layouts/Operational_Sidebar_Progress.component";
import { API_URL } from '../../constant/API_Settings';
import swal from "sweetalert";
import { useHistory } from 'react-router-dom';
import LoginHeaderOperational from './LoginHeaderOperational';
import Modal from "react-modal";
import Loader from './Employee/Loader.component';
import { applyCheckUrlNavigation } from '../../utils/checkUrlNavigation';
import { formatOperationalDate, toDateInputValue } from '../../utils/openingBalanceDate';
import './operational-dates.css';

function formatLongDate(value) {
	const ymd = toDateInputValue(value);
	if (!ymd) {
		return '';
	}
	const [year, month, day] = ymd.split('-').map(Number);
	const date = new Date(year, month - 1, day);
	if (Number.isNaN(date.getTime())) {
		return formatOperationalDate(value);
	}
	return date.toLocaleDateString('en-GB', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	});
}

function formatWeekday(value) {
	const ymd = toDateInputValue(value);
	if (!ymd) {
		return '';
	}
	const [year, month, day] = ymd.split('-').map(Number);
	const date = new Date(year, month - 1, day);
	if (Number.isNaN(date.getTime())) {
		return '';
	}
	return date.toLocaleDateString('en-GB', { weekday: 'long' });
}

const OperationalDate = (props) => {
	const [input, setInput] = useState({});
	const [permission, setPermission] = useState([]);
	const [note, setNote] = useState({});
	const [loading, setLoading] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [isPriceOpen, setIsPriceOpen] = useState(false);
	const [saving, setSaving] = useState(false);
	const [showViewEdit, setShowViewEdit] = useState(false);
	const history = useHistory();

	function authHeaders() {
		return {
			'Access-Control-Allow-Origin': '*',
			'Content-Type': 'application/json',
			Authorization: `Bearer ${localStorage.getItem('AUTH_TOKEN') || ''}`,
			Accept: 'application/json',
		};
	}

	const handleChange = (e) => {
		setInput({ ...input, op_date: e.target.value });
		if (e.target.value) {
			show_btn(e.target.value);
		} else {
			setShowViewEdit(false);
		}
	};

	function toggleModal() {
		setIsOpen(!isOpen);
	}

	const handleSubmit = () => {
		if (!input.op_date) {
			return;
		}
		toggleModal();
	};

	useEffect(() => {
		check_url();
	}, []);

	function check_url() {
		const requestOptions = {
			method: 'post',
			headers: {
				"Access-Control-Allow-Origin": "*",
				"Content-Type": "application/json",
				"Authorization": "Bearer " + localStorage.getItem("AUTH_TOKEN") + "",
				"Accept": "application/json"
			},
			body: JSON.stringify({
				pathurl: window.location.pathname,
			}),
		};
		fetch(`${API_URL}/check_url`, requestOptions)
			.then(response => { return response.json(); })
			.then(responseData => {
				applyCheckUrlNavigation(
					history,
					responseData,
					window.location.pathname,
					getDate
				);
				return responseData;
			})
			.catch(() => {});
	}

	function getDate() {
		setShowViewEdit(false);
		const requestOptions = {
			method: 'post',
			headers: {
				"Access-Control-Allow-Origin": "*",
				"Content-Type": "application/json",
				"Authorization": "Bearer " + localStorage.getItem("AUTH_TOKEN") + "",
				"Accept": "application/json"
			},
			body: JSON.stringify({
				next_date: input.op_date
			})
		};
		fetch(`${API_URL}/get-next-date`, requestOptions)
			.then(response => { return response.json(); })
			.then(responseData => {
				if (responseData.status == 3) {
					setPermission({ ...permission, status: responseData.status, message: responseData.message });
				}
				const opDate = toDateInputValue(responseData.op_date);
				setInput({
					...input,
					next_date: toDateInputValue(responseData.next_date),
					config_date: toDateInputValue(responseData.confg_date),
					op_date: opDate,
				});
				setLoading(true);
				setNote({
					price_change: responseData.price_change
				});
				if (opDate) {
					show_btn(opDate);
				}
				return responseData;
			})
			.catch(() => {});
	}

	function rememberWorkingOpDate(date) {
		const ymd = toDateInputValue(date);
		if (!ymd) {
			return;
		}
		localStorage.setItem('m_op_date', ymd);
		localStorage.setItem('op_date', ymd);
	}

	function handleYes() {
		if (!input.op_date || saving) {
			return;
		}

		setSaving(true);
		fetch(`${API_URL}/list-own-fuels`, {
			method: 'post',
			headers: authHeaders(),
			body: JSON.stringify({ next_date: input.op_date }),
		})
			.then((response) => response.json().then((body) => ({ ok: response.ok, body })))
			.then(({ ok, body: responseData }) => {
				if (!ok) {
					swal('', responseData.message || 'Could not open this operational date', 'error');
					setIsOpen(false);
					return;
				}
				if (responseData.status === false) {
					swal('', responseData.message || 'Could not open this operational date', 'warning');
					setIsOpen(false);
					return;
				}

				rememberWorkingOpDate(input.op_date);

				if (responseData.status == 2) {
					setIsOpen(false);
					history.push('/nozzle-employee-list');
					return;
				}

				setIsOpen(false);
				setIsPriceOpen(true);
			})
			.catch(() => {
				swal('', 'Could not open this operational date', 'error');
			})
			.finally(() => setSaving(false));
	}

	function handleNo() {
		toggleModal();
	}

	function goToFuelPriceChange() {
		if (!input.op_date || saving) {
			return;
		}

		setSaving(true);
		fetch(`${API_URL}/save_next_date_to_meta`, {
			method: 'post',
			headers: authHeaders(),
			body: JSON.stringify({ next_date: input.op_date }),
		})
			.then((response) => response.json())
			.then((responseData) => {
				setIsPriceOpen(false);
				if (responseData.status == true) {
					rememberWorkingOpDate(input.op_date);
					history.push('/fuel-sell-price');
					return;
				}
				swal('', responseData.message || 'Could not open fuel price change', 'warning');
				history.push('/operational-date');
			})
			.catch(() => {
				setIsPriceOpen(false);
				swal('', 'Something went wrong while opening fuel price change', 'error');
			})
			.finally(() => setSaving(false));
	}

	function skipFuelPriceChange() {
		if (!input.op_date || saving) {
			return;
		}

		setSaving(true);
		fetch(`${API_URL}/list-own-fuel`, {
			method: 'post',
			headers: authHeaders(),
			body: JSON.stringify({
				next_date: input.op_date,
				change: 'no',
			}),
		})
			.then((response) => response.json())
			.then((responseData) => {
				setIsPriceOpen(false);
				if (responseData.status === false) {
					swal('', responseData.message || 'Could not confirm fuel prices', 'warning');
					return;
				}
				rememberWorkingOpDate(input.op_date);
				swal('', responseData.message || 'Previous fuel prices carried forward for today', 'success');
				history.push('/nozzle-employee-list');
			})
			.catch(() => {
				setIsPriceOpen(false);
				swal('', 'Something went wrong while confirming fuel prices', 'error');
			})
			.finally(() => setSaving(false));
	}

	function show_btn(c_date) {
		const requestOptions = {
			method: 'post',
			headers: {
				"Access-Control-Allow-Origin": "*",
				"Content-Type": "application/json",
				"Authorization": "Bearer " + localStorage.getItem("AUTH_TOKEN") + "",
				"Accept": "application/json"
			},
			body: JSON.stringify({
				c_date: c_date
			})
		};
		fetch(`${API_URL}/show_btn`, requestOptions)
			.then(response => { return response.json(); })
			.then(responseData => {
				setShowViewEdit(responseData.status == true);
				return responseData;
			})
			.catch(() => {});
	}

	const selectedLabel = formatLongDate(input.op_date) || 'Not set';
	const selectedWeekday = formatWeekday(input.op_date);
	const earliestLabel = formatOperationalDate(input.config_date) || '—';
	const latestLabel = formatOperationalDate(input.next_date) || '—';

	return (
		<>
			<div className="main-container page-wrap op-dates-page">
				<div className="d-md-flex">
					<Sidebar_Progress props={props} progress={100} operation={1} />
					<div className="content-area">
						<LoginHeaderOperational props={props} page_title="Operational date" />
						<div className="container pb-3">
							{permission.status == 3 ? (
								<div className="form-wrap op-dates-permission mt-5">
									<h3>Access needed</h3>
									<p className="mb-0">{permission.message}</p>
								</div>
							) : (
								<div className="row mt-md-4 mt-3 form-container justify-content-center">
									<div className="col-md-8 col-lg-7">
										{!loading ? (
											<Loader />
										) : (
											<div className="form-wrap op-dates-card">
												<div className="op-dates-hero">
													<span className="op-dates-hero-icon" aria-hidden="true">
														<i className="fa fa-calendar" />
													</span>
													<div>
														<h3>Choose the working day</h3>
														<p>All sales, receipts, and day close will be recorded against this operational date.</p>
													</div>
												</div>

												<div className="op-dates-body">
													<div className="op-dates-current">
														<span className="op-dates-current-label">Selected date</span>
														<strong className="op-dates-current-value">{selectedLabel}</strong>
														{selectedWeekday ? (
															<span className="op-dates-current-weekday">{selectedWeekday}</span>
														) : null}
													</div>

													<div className="op-dates-meta">
														<div className="op-dates-meta-item">
															<span>Earliest</span>
															<strong>{earliestLabel}</strong>
														</div>
														<div className="op-dates-meta-item">
															<span>Latest</span>
															<strong>{latestLabel}</strong>
														</div>
													</div>

													<div className="op-dates-field">
														<label htmlFor="op-date-input">Operational date</label>
														<input
															id="op-date-input"
															type="date"
															className="form-control op-dates-input"
															name="startDate"
															min={input.config_date || undefined}
															max={input.next_date || undefined}
															onChange={handleChange}
															value={input.op_date || ''}
														/>
														<p className="op-dates-hint">
															Pick a date between {earliestLabel} and {latestLabel}.
														</p>
													</div>

													<div className="op-dates-actions">
														<button
															type="button"
															className="btn btn-primary op-dates-btn op-dates-btn-primary"
															onClick={handleSubmit}
															disabled={!input.op_date}
														>
															Confirm date
														</button>
														{showViewEdit ? (
															<button
																type="button"
																className="btn op-dates-btn op-dates-btn-secondary"
																onClick={handleYes}
															>
																View / Edit
															</button>
														) : null}
													</div>

													{note.price_change == 'NO' ? (
														<div className="op-dates-alert" role="status">
															<i className="fa fa-info-circle" aria-hidden="true" />
															Confirm the selling price on this date before moving to other screens.
														</div>
													) : null}
												</div>
											</div>
										)}
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			<Modal
				isOpen={isOpen}
				onRequestClose={toggleModal}
				contentLabel="Confirm operational date"
				className="mymodal custommodal op-dates-ask-modal"
				overlayClassName="myoverlay"
			>
				<div className="modal-dialogue-xl">
					<div className="modal-content">
						<div className="modal-title modal-header">
							<h5 className="mb-0">Confirm operational date</h5>
							<button type="button" onClick={toggleModal} className="btn-close" aria-label="Close" />
						</div>
						<div className="modal-body">
							<p className="mb-0">Use this date for today’s operations?</p>
							<p className="op-dates-modal-date">{selectedWeekday ? `${selectedWeekday}, ` : ''}{selectedLabel}</p>
						</div>
						<div className="modal-footer op-dates-ask-footer">
							<button type="button" className="btn btn-primary" onClick={handleYes} disabled={saving}>
								{saving ? 'Opening…' : 'Yes, confirm'}
							</button>
							<button type="button" className="btn btn-secondary" onClick={handleNo} disabled={saving}>Cancel</button>
						</div>
					</div>
				</div>
			</Modal>

			<Modal
				isOpen={isPriceOpen}
				onRequestClose={() => !saving && setIsPriceOpen(false)}
				contentLabel="Fuel selling price change"
				className="mymodal custommodal op-dates-ask-modal"
				overlayClassName="myoverlay"
			>
				<div className="modal-dialogue-xl">
					<div className="modal-content">
						<div className="modal-title modal-header">
							<h5 className="mb-0">Fuel selling price</h5>
							<button type="button" onClick={() => setIsPriceOpen(false)} className="btn-close" aria-label="Close" disabled={saving} />
						</div>
						<div className="modal-body">
							<p className="mb-2">Do you have a fuel selling price change for this date?</p>
							<p className="op-dates-modal-date">{selectedWeekday ? `${selectedWeekday}, ` : ''}{selectedLabel}</p>
							<p className="op-dates-price-hint mb-0">
								Choose <strong>Yes</strong> to enter new selling prices. Choose <strong>No</strong> to keep yesterday’s prices.
							</p>
						</div>
						<div className="modal-footer op-dates-ask-footer">
							<button type="button" className="btn btn-primary" onClick={goToFuelPriceChange} disabled={saving}>
								{saving ? 'Please wait…' : 'Yes, change prices'}
							</button>
							<button type="button" className="btn btn-secondary" onClick={skipFuelPriceChange} disabled={saving}>
								No, keep previous prices
							</button>
						</div>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default OperationalDate;
